import httpClient from '../../httpClient';
import { Listing, ListingEnquiry } from '../../../models/Listing';
import { PaginatedData, PaginationParams } from 'models/PaginatedData';
import { Params } from 'react-router-dom';
import { Lead, LeadStages, SoftSignalPayload } from 'models/Lead';
import { DropzoneFile } from 'components/Dropzone/utils';
import { AxiosRequestConfig } from 'axios';
import { DealRoom } from 'models/DealRoom';
import { DataRoomInvitation } from 'router/subrouters/DataRoom/models/DataRoom';
import { BuyerListingStats } from 'router/subrouters/Dash/subrouters/private/subrouters/Portfolio/components/PortfolioFilterBar/models';
import { NudgeNotification } from 'models/Notification';
import { getFileParams, getUploadedFile } from 'uploadFileUtils';
import { UploadedFileResponse } from 'models/File';

export type PortfoilioLead = {
  _id: string;
  listing: Listing;
  listingEnquiry: ListingEnquiry;
  dealRoom: DealRoom;
  buyerStep: LeadStages;
  progressedStep?: number;
  confirmedInterest?: boolean;
  withdrawnInterest?: boolean;
  nudgeNotification?: NudgeNotification;
  lastSeen?: Date;
  createdAt: Date;
};

function getBuyingPortfolio(enquiryId: string, params?: PaginationParams) {
  return httpClient.get<{ lead: Lead }>(
    `portfolio/enquiries/${enquiryId}/buyer`,
    {
      params: {
        $relations: '["listing","listingEnquiry","dealRoom"]',
        ...params,
      },
    },
  );
}

function getWatchlistedListings(params?: PaginationParams) {
  return httpClient.get<PaginatedData<PortfoilioLead>>(`listings/watchlist`, {
    params,
  });
}

function getProspectiveBuyersListings(params?: PaginationParams) {
  return httpClient.get<PaginatedData<PortfoilioLead>>(
    `listings/prospective-buying`,
    {
      params,
    },
  );
}

function buyerListingStats(params?: Params) {
  return httpClient.get<BuyerListingStats>(`portfolio/buyer-listing-stats`, {
    params,
  });
}

function withdrawInterest(leadId: string) {
  return httpClient.post(`portfolio/leads/${leadId}/withdraw-interest`);
}

function confirmInterest(leadId: string) {
  return httpClient.post(`portfolio/leads/${leadId}/confirm-interest`);
}

function submitNBIO(leadId: string, file: DropzoneFile) {
  return httpClient.post(`portfolio/leads/${leadId}/submit-nbio`, file);
}

async function uploadNBIODocument(
  file: File,
  leadId: string,
  config: AxiosRequestConfig,
) {
  const fileParams = getFileParams('NBIO', file);

  const { data } = await httpClient.post<UploadedFileResponse>(
    `portfolio/leads/${leadId}/upload-temporary-object`,
    fileParams,
  );

  return await getUploadedFile(data, file, config);
}

function deleteNBIODocument(leadId: string, fileId: string) {
  return httpClient.delete(`portfolio/leads/${leadId}/files/NBIO/${fileId}`);
}

function getDataRoomInvitations(params?: PaginationParams) {
  return httpClient.get<PaginatedData<DataRoomInvitation>>(
    `data-room-member-invitations`,
    {
      params,
    },
  );
}

function softSignalReact(leadId: string, data: SoftSignalPayload) {
  return httpClient.post(`portfolio/leads/${leadId}/soft-signal`, data);
}

function softSignalReactUndo(leadId: string) {
  return httpClient.post(`portfolio/leads/${leadId}/soft-signal-undo`);
}

export default {
  getWatchlistedListings,
  getBuyingPortfolio,
  buyerListingStats,
  withdrawInterest,
  confirmInterest,
  submitNBIO,
  deleteNBIODocument,
  uploadNBIODocument,
  getProspectiveBuyersListings,
  getDataRoomInvitations,
  softSignalReact,
  softSignalReactUndo,
};
